/**
 * Quote Validation Utilities
 * Validates quote settings and data
 */

/**
 * Validates quote title
 * @param {string} title - Quote title to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateQuoteTitle = (title) => {
  if (!title || title.trim() === '') {
    return 'Quote title is required';
  }

  if (title.trim().length < 3) {
    return 'Quote title must be at least 3 characters';
  }

  if (title.length > 100) {
    return 'Quote title must not exceed 100 characters';
  }

  return null;
};

/**
 * Validates date is in the future
 * @param {string} date - ISO date string
 * @returns {string|null} Error message or null if valid
 */
export const validateFutureDate = (date, fieldName = 'Date') => {
  if (!date) {
    return `${fieldName} is required`;
  }

  const selectedDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (selectedDate < today) {
    return `${fieldName} must be in the future`;
  }

  return null;
};

/**
 * Validates valid until date
 * @param {string} date - Valid until date
 * @returns {string|null} Error message or null if valid
 */
export const validateValidUntilDate = (date) => {
  return validateFutureDate(date, 'Valid until date');
};

/**
 * Validates expiration date
 * @param {string} date - Expiration date
 * @returns {string|null} Error message or null if valid
 */
export const validateExpirationDate = (date) => {
  return validateFutureDate(date, 'Expiration date');
};

/**
 * Validates notes field
 * @param {string} notes - Notes text
 * @returns {string|null} Error message or null if valid
 */
export const validateNotes = (notes) => {
  if (notes && notes.length > 1000) {
    return 'Notes must not exceed 1000 characters';
  }

  return null;
};

/**
 * Validates complete quote settings
 * @param {Object} settings - Quote settings object
 * @returns {Object} Object with field errors
 */
export const validateQuoteSettings = (settings) => {
  const errors = {};

  const titleError = validateQuoteTitle(settings?.bidTitle);
  if (titleError) {
    errors.bidTitle = titleError;
  }

  // Valid until date is required
  const validUntilError = validateValidUntilDate(settings?.validUntil);
  if (validUntilError) {
    errors.validUntil = validUntilError;
  }

  // NOTE: `settings.expirationDate` was a separate field that's been
  // deprecated — the UI now uses a single "Expiration Date *" input
  // that writes to `settings.validUntil` (see QuoteSettingsSection).
  // Validating expirationDate here caused a stuck error in edit mode
  // where the hydrated value stayed in the past while broker edits
  // only moved validUntil. Removed intentionally; validateExpirationDate
  // helper is still exported for anyone else who might use it.

  const notesError = validateNotes(settings?.notes);
  if (notesError) {
    errors.notes = notesError;
  }

  return errors;
};

/**
 * Checks if quote settings are complete and valid
 * @param {Object} settings - Quote settings object
 * @returns {boolean} True if settings are complete and valid
 */
export const isQuoteSettingsComplete = (settings) => {
  if (!settings) return false;

  const errors = validateQuoteSettings(settings);

  // Must have no errors and all required fields filled
  // bidTitle and validUntil are required
  return (
    Object.keys(errors).length === 0 &&
    settings.bidTitle &&
    settings.bidTitle.trim() !== '' &&
    settings.validUntil
  );
};

/**
 * Validates complete quote data before sending
 * @param {Object} quoteData - Complete quote data
 * @returns {Object} Object with validation errors
 */
export const validateCompleteQuote = (quoteData) => {
  const errors = {
    client: null,
    flights: null,
    aircraft: null,
    settings: null,
  };

  if (!quoteData.client || !quoteData.client.clientData) {
    errors.client = 'Client information required';
  }

  if (!quoteData.flights || quoteData.flights.length === 0) {
    errors.flights = 'Flight details required';
  }

  if (!quoteData.aircraft) {
    errors.aircraft = 'Aircraft selection required';
  }

  if (!isQuoteSettingsComplete(quoteData.settings)) {
    errors.settings = 'Quote settings incomplete';
  }

  // Remove null errors
  Object.keys(errors).forEach(key => {
    if (!errors[key]) delete errors[key];
  });

  return errors;
};

/**
 * Checks if complete quote is valid and ready to send
 * @param {Object} quoteData - Complete quote data
 * @returns {boolean} True if quote is valid and ready
 */
export const isQuoteReadyToSend = (quoteData) => {
  const errors = validateCompleteQuote(quoteData);
  return Object.keys(errors).length === 0;
};

// Helper function to replace email placeholders with actual values
export const replaceEmailPlaceholders = (emailMessage, values) => {
  if (!emailMessage) return emailMessage;

  return emailMessage
    .replace(/\{\{client_name\}\}/g, values.clientName || '')
    .replace(/\{\{quote_number\}\}/g, values.quoteNumber || '')
    .replace(/\{\{quote_link_address\}\}/g, values.quoteLink || '')
    .replace(/\{\{quote_link\}\}/g, 'View Quote')
    .replace(/\{\{request_link\}\}/g, 'View Page');
};
